import { onScopeDispose } from "vue";

type EventMap = {
  [eventName: string]: unknown[];
};

type EventCallback<T extends unknown[]> = (...args: T) => void;

type EventEmitter<T extends EventMap> = {
  on<K extends keyof T>(eventName: K, callback: EventCallback<T[K]>): void;
  off<K extends keyof T>(eventName: K, callback: EventCallback<T[K]>): void;
  once<K extends keyof T>(eventName: K, callback: EventCallback<T[K]>): void;
  emit<K extends keyof T>(eventName: K, ...args: T[K]): void;
};

export function useEventEmitter<M extends EventMap>(): EventEmitter<M> {
  const listeners: Partial<M> = {};

  function on<K extends keyof M>(eventName: K, callback: EventCallback<M[K]>) {
    if (!listeners[eventName]) {
      Object.assign(listeners, { [eventName]: [] });
    }
    listeners[eventName]?.push(callback);
  }

  function off<K extends keyof M>(eventName: K, callback: EventCallback<M[K]>) {
    const eventListeners = listeners[eventName];
    if (!eventListeners) {
      return;
    }
    const index = eventListeners.indexOf(callback);
    if (index !== -1) {
      eventListeners.splice(index, 1);
    }
  }

  function once<K extends keyof M>(
    eventName: K,
    callback: EventCallback<M[K]>
  ) {
    function wrappedCallback(...args: M[K]) {
      off(eventName, wrappedCallback);
      callback(...args);
    }
    on(eventName, wrappedCallback);
  }

  function emit<K extends keyof M>(eventName: K, ...args: M[K]) {
    const eventListeners = listeners[eventName];
    if (!eventListeners) {
      return;
    }
    eventListeners.forEach((callback) => {
      if (typeof callback !== "function") {
        return;
      }
      callback(...args);
    });
  }

  onScopeDispose(() => {
    for (const eventName in listeners) {
      const eventListeners = listeners[eventName];
      if (eventListeners) {
        eventListeners.length = 0;
      }
    }
  });

  return {
    on,
    off,
    once,
    emit
  };
}
